// Modules
import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
/* Table from MUI */
// import Tab from "@material-ui/core/Tab";
import TabContext from "@material-ui/lab/TabContext";
// import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";

// Context
import ApiContext from "../../../contexts/ApiContext";

// Local
import CustomTableRow from "./CustomTableRow";
import Graphs from "./Graphs";
import Divider from "../../utils/Divider";
import HouseCheckmark from "../../../assets/img/svg/House_checkmark";

// Assets
import "./results.scss";
import ApartmentIcon from "@material-ui/icons/Apartment";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import EventIcon from "@material-ui/icons/Event";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

const Results = (props) => {
	const { isMobile, isTablet } = useContext(ApiContext);
	const {
		data,
		loader,
		valuesAPI,
		valuesSimulation,
		hasDataChanged,
		fraisCourtageSwitch,
	} = props;
	/* Fixed data from API, not impacted by the changes in the simulation. */
	const {
		amenagement_interieur_ttc,
		honoraire_bevouac_ttc,
		mise_vente_fai,
		negociation,
		achat_fai,
		frais_notaire,
		caution_frais_dossier,
		montant_travaux_ttc,
		charges_reccurentes_mois,
		charges_aleatoires_mois,
		rendement_brut,
		taux_livret_a,
		loyer_cc,
		charges_copro_mois,
		taxe_fonciere_mois,
		pno_mois,
		gestion_locative_mois,
		frais_mise_en_location_mois,
		provisions_pour_imprevus_mois,
		vacance_locative_mois,
		expert_comptable_mois,
		revalorisation,
		total_revenus_annuels,
		charges_reccurentes_annuelles,
		rendement_agglo,
		arg_b1,
		arg_b2,
		arg_b3,
	} = data;
	/* Volatile data, is impacted by the simulation. */
	const {
		bilan_mensuel,
		gain_net_revente,
		mensualite,
		montant_emprunte,
		prix_revente,
		rente_nette,
		total_acquisition,
		total_depenses_mois, // eslint-disable-line
		graphs,
	} = valuesAPI;
	const {
		frais_courtage,
		mise_depart,
		// duree_emprunt,
		// taux_emprunt,
		// taux_assurance,
		duree_revente,
	} = valuesSimulation;
	const acquisition_total = fraisCourtageSwitch === true ? (achat_fai + frais_notaire + frais_courtage + caution_frais_dossier) : (achat_fai + frais_notaire + caution_frais_dossier);

	/* Variables state V2 */
	const [tabView, setTabView] = useState("table"); // eslint-disable-line
	/* Sub-tables of each accordion header */
	const acquisition_details = [
		{ poste: "Prix de vente FAI", val: mise_vente_fai, changed: false },
		{ poste: "- Négociation envisagée", val: negociation, changed: false },
		{ poste: "= Prix d'achat envisagé", val: achat_fai, changed: false },
		{ poste: "+ Frais de notaire", val: frais_notaire, changed: false },
		{ poste: "+ Frais de courtage", val: frais_courtage, changed: hasDataChanged.frais_courtage },
		{ poste: "+ Frais garantie bancaire", val: caution_frais_dossier, changed: false },
	];
	const construction_details = [
		{ poste: "Travaux", val: montant_travaux_ttc, changed: false },
		{ poste: "Mobilier et équipement", val: amenagement_interieur_ttc, changed: false },
	];
	const recurring_charges_details = [
		{ poste: "Charges de copropriété", val: charges_copro_mois, changed: false },
		{ poste: "Taxe foncière", val: taxe_fonciere_mois, changed: false },
		{ poste: "Assurance propriétaire (PNO)", val: pno_mois, changed: false },
		{ poste: "Frais de gestion locative", val: gestion_locative_mois, changed: false },
	];
	const hazards_expenses_details = [
		{ poste: "Frais de mise en location", val: frais_mise_en_location_mois, changed: false },
		{ poste: "Provisions pour dépenses imprévues", val: provisions_pour_imprevus_mois, changed: false },
		{ poste: "Vacance locative estimée (0.5 mois)", val: vacance_locative_mois, changed: false },
		{ poste: "Frais d’expertise comptable", val: expert_comptable_mois, changed: false },
	];
	const resale_details = [
		{ poste: "Valorisation annuelle du bien", val: (revalorisation * 100), changed: false },
		{ poste: "+ Prix de revente", val: prix_revente ?? 0, changed: hasDataChanged.prix_revente },
		{ poste: "- Épargne mensuelle placée ou cashflow", val: bilan_mensuel ?? 0, changed: hasDataChanged.bilan_mensuel },
		{ poste: "- Mise de départ", val: mise_depart, changed: hasDataChanged.mise_depart },
	];
	const location_details = [
		{ poste: "Revenus annuels", val: total_revenus_annuels, changed: false },
		{ poste: "- Charges récurrentes annuelles", val: charges_reccurentes_annuelles, changed: false },
		{ poste: "- Charges aléatoires annuelles", val: (charges_aleatoires_mois * 12), changed: false },
	];
	const return_details = [
		{ poste: "Rendement brut moyen agglo", val: (rendement_agglo * 100), changed: false },
		{ poste: "Rendement brut moyen France", val: rendement_brut, changed: false },
	];

	/**
	 * Creates an object.
	 * @param {String} keyProp : destined to be a key in a map.
	 * @param {String} rubrique : header name for the accordion.
	 * @param {Number} total : total of all the values displayed in the sub-table.
	 * @param {Object} detail : object that represents the sub-table.
	 * @returns an object.
	 */
	const createData = (keyProp, rubrique, total, detail, hasChanged) => {
		return {
			keyProp,
			rubrique,
			total,
			detail,
			hasChanged,
		};
	};

	/* "Le projet" */
	const rowsProject = [
		createData("acquisition", "Acquisition", acquisition_total, acquisition_details, hasDataChanged.frais_courtage),
		createData("amenagement-ttc", "Aménagement", (amenagement_interieur_ttc + montant_travaux_ttc), construction_details, false),
		createData("honoraires-bevouac-ttc", "Honoraires Bevouac", honoraire_bevouac_ttc, [], false),
		createData("budget-total", "Budget total du projet", total_acquisition ?? 0, [], hasDataChanged.total_acquisition),
	];
	/* "Hypothèse de financement" */
	const rowsFinance = [
		createData("montant-empruntee", "Montant emprunté", montant_emprunte ?? 0, [], hasDataChanged.montant_emprunte),
		createData("mensualite-emprunt", "Mensualité emprunt", mensualite ?? 0, [], hasDataChanged.mensualite),
	];
	/* "Dépenses récurrentes" */
	const rowsRecurringExpenses = [
		createData("mensualite-emprunt-2", "Mensualité emprunt", mensualite ?? 0, [], hasDataChanged.mensualite),
		createData("charges-reccurentes", "Charges récurrentes", charges_reccurentes_mois, recurring_charges_details, false),
		createData("charges-aleatoires", "Charges aléatoires", charges_aleatoires_mois, hazards_expenses_details, false),
	];
	/* Épargne mensuelle placée ou cashflow */
	const rowMonthlyCashflow = [
		createData("bilan-mensuel", "Épargne mensuelle placée ou cashflow", bilan_mensuel ?? 0, [], hasDataChanged.bilan_mensuel),
	];
	/* "Revenus" */
	const rowsIncomes = [
		createData("loyer-mensuel", "Loyer mensuel", loyer_cc, [], false)
	];
	/* "Projection dans 20 ans" */
	const rowsProjection = [
		createData("revente", "Gain net à la revente", gain_net_revente ?? 0, resale_details, hasDataChanged.gain_net_revente),
		createData("location", "Rente nette après remboursement de l’emprunt", rente_nette ?? 0, location_details, hasDataChanged.rente_nette),
	];
	/* "Rendement" */
	const rowsReturn = [
		createData("rendement-brut-investissement", "Rendement brut de l'investissement", rendement_brut, return_details, false),
		createData("rendement-livret-a", "Rendement du Livret A", (taux_livret_a * 100), [], false),
	];

	/**
	 * FOR V2 : Switch between tabs on click.
	 * @param {Event} event 
	 * @param {String} newTab : name of the clicked tab
	 */
	// const handleTabView = (event, newTab) => {
	//   setTabView(newTab);
	// };

	/**
	 * 
	 * @param {*} x 
	 * @param {*} y 
	 * @returns {Number}
	 */
	const numRoundMultiple = (x, y) => {
		return Math.round(x / y) * y;
	}

	return (
		<div className={`balance-sheet-results-container ${isMobile === true || isTablet === true ? "mobile" : ""} ${isTablet === true ? "tablet" : ""}`}>
			<TabContext value={tabView} className="balance-sheet-results-container">
				{/* ONLY FOR V2 : Tabs */}
				{/* <AppBar className="app-bar" position="static">
                <TabList onChange={handleTabView}>
                    <Tab
                        // /!\ id ?
                        className="tab"
                        label={
                            <span id="resp_span">
                                Vue tableur
                            </span>
                        }
                        value="table"
                    />
                    <Tab
                        // /!\ id ?
                        className="tab"
                        label={
                            <span id="resp_span">
                                Vue graphique
                            </span>
                        }
                        value="graphic"
                    />
                </TabList>
            </AppBar> */}

				{/* Table view */}
				<TabPanel value="table" className="table-view-container">

					{/* Acquiring & Funding */}
					<div className="first-column">
						<h5 className="column-title">Acquisition et financement</h5>

						<div className="sub-tables-container">
							<div className="sub-table-container">
								<h6 className="section-title">
									<ApartmentIcon />
									Le projet
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsProject.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>

							<div className="sub-table-container">
								<h6 className="section-title">
									<AccountBalanceIcon />
									Hypothèse de financement
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsFinance.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>
						</div>
					</div>

					{/* Balance sheet */}
					<div className="second-column">
						<h5 className="column-title">Bilan</h5>

						<div className="sub-tables-container">
							<div className="sub-table-container">
								<h6 className="section-title">
									<AutorenewIcon />
									Dépenses récurrentes
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsRecurringExpenses.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
								<TableContainer className="sub-table bilan-mensuel">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowMonthlyCashflow.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>

							<div className="sub-table-container">
								<h6 className="section-title">
									<MonetizationOnIcon />
									Revenus
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsIncomes.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>
						</div>
					</div>

					{/* Financial analyse */}
					<div className="third-column">
						<h5 className="column-title">Analyse financière</h5>

						<div className="sub-tables-container">
							<div className="sub-table-container">
								<h6 className="section-title">
									<EventIcon />
									Projection dans {duree_revente} ans
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsProjection.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>

							<div className="sub-table-container">
								<h6 className="section-title">
									<TrendingUpIcon />
									Rendement
								</h6>
								<TableContainer className="sub-table">
									<Table aria-label="collapsible table">
										<TableBody>
											{rowsReturn.map((row) => (
												<React.Fragment key={row.keyProp}>
													<CustomTableRow
														keyProp={row.keyProp}
														loader={loader}
														row={row}
													/>
												</React.Fragment>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>
						</div>
					</div>

					<Graphs
						graphs={graphs}
						loader={loader}
					/>
				</TabPanel>

				{/* TO DO FOR A V2 : Graphic view */}
				{/* <TabPanel value="graphic">
				<Grid container spacing={1}></Grid>
				<Grid container spacing={1}>
					<Grid item xs={4}>
						<Paper>
							
						</Paper>
					</Grid>

					<Grid item xs={4}>
						
						<Typography>tsghd</Typography>
					</Grid>
					<Grid container spacing={1}>
						<Box></Box>
					</Grid>
				</Grid>
			</TabPanel> */}
			</TabContext>

			<Divider text="Analyse globale" loader={loader} />
			<p className="global-analysis">
				Cet investissement, rapportera près de
				<strong className="emphasis">
					{new Intl.NumberFormat(
						"fr-FR",
						{ style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }
					).format(gain_net_revente)}
				</strong>
				(Gain net) dans votre poche* si vous l’achetez aujourd’hui et le revendez dans
				<strong className="emphasis">{duree_revente}</strong>
				ans.
				Cela en fait un très bon placement,
				<strong className="emphasis">
					{gain_net_revente && graphs.comparatif_livret_a?.data[0]?.y[0]
						? numRoundMultiple(gain_net_revente / graphs.comparatif_livret_a?.data[0]?.y[0], 1)
						: 0}
				</strong>
				fois plus intéressant que d’investir le même argent dans votre Livret A (gain net seulement de :
				<strong className="emphasis no-space-right">
					{graphs.comparatif_livret_a?.data[0]?.y[0]
						? (
							new Intl.NumberFormat(
								"fr-FR",
								{ style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }
							).format(graphs.comparatif_livret_a?.data[0]?.y[0])
						) : 0}
				</strong>
				).
				{arg_b1 && arg_b2 && arg_b3
					? "Au delà d’être situé dans une ville identifiée par Bevouac pour son dynamisme futur, nous l’avons sélectionné car :"
					: null}
			</p>

			{arg_b1 && arg_b2 && arg_b3
				? (
					<ul className="is-good-investment-container">
						<li className="is-good-investment-wrapper">
							<HouseCheckmark />
							<p>{arg_b1}</p>
						</li>
						<li className="is-good-investment-wrapper">
							<HouseCheckmark />
							<p>{arg_b2}</p>
						</li>
						<li className="is-good-investment-wrapper">
							<HouseCheckmark />
							<p>{arg_b3}</p>
						</li>
					</ul>
				) : null}
		</div>
	);
};

Results.propTypes = {
	data: PropTypes.object.isRequired,
	loader: PropTypes.bool.isRequired,
	valuesAPI: PropTypes.exact({
		bilan_mensuel: PropTypes.number,
		gain_net_revente: PropTypes.number,
		mensualite: PropTypes.number,
		montant_emprunte: PropTypes.number,
		prix_revente: PropTypes.number,
		rente_nette: PropTypes.number,
		total_acquisition: PropTypes.number,
		total_depenses_mois: PropTypes.number,
		graphs: PropTypes.exact({
			comparatif_livret_a: PropTypes.object,
			comparatif_mensualite_loyer: PropTypes.object,
			evolution_gain_net: PropTypes.object,
			levier_bancaire: PropTypes.object,
		}),
	}),
	valuesSimulation: PropTypes.exact({
		frais_courtage: PropTypes.number.isRequired,
		mise_depart: PropTypes.number.isRequired,
		duree_emprunt: PropTypes.number.isRequired,
		taux_emprunt: PropTypes.number.isRequired,
		taux_assurance: PropTypes.number.isRequired,
		duree_revente: PropTypes.number.isRequired,
	}),
	hasDataChanged: PropTypes.exact({
		bilan_mensuel: PropTypes.bool,
		gain_net_revente: PropTypes.bool,
		mensualite: PropTypes.bool,
		montant_emprunte: PropTypes.bool,
		prix_revente: PropTypes.bool,
		rente_nette: PropTypes.bool,
		total_acquisition: PropTypes.bool,
		total_depenses_mois: PropTypes.bool,
		frais_courtage: PropTypes.bool,
		mise_depart: PropTypes.bool,
		duree_emprunt: PropTypes.bool,
		taux_emprunt: PropTypes.bool,
		taux_assurance: PropTypes.bool,
		duree_revente: PropTypes.bool,
		fraisCourtage: PropTypes.bool,
		effortEpargne: PropTypes.bool,
		equilibre: PropTypes.bool,
		graphs: PropTypes.bool,
	}),
	fraisCourtageSwitch: PropTypes.bool.isRequired,
};

Results.defaultProps = {
	valuesAPI: {
		bilan_mensuel: 0,
		gain_net_revente: 0,
		mensualite: 0,
		montant_emprunte: 0,
		prix_revente: 0,
		rente_nette: 0,
		total_acquisition: 0,
		total_depenses_mois: 0,
		graphs: {},
	},
};

export default Results;
